<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import CustomCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CustomCampaign/CustomCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import CustomCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/CustomCampaignEmptyState.vue';

const { t } = useI18n();
const getters = useStoreGetters();

const selectedCampaign = ref(null);
const [showCustomCampaignDialog, toggleCustomCampaignDialog] = useToggle();

// Debug: log when dialog state changes
const handleToggleDialog = value => {
  console.log('Toggle Custom Campaign Dialog:', value);
  toggleCustomCampaignDialog(value);
};

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const confirmDeleteCampaignDialogRef = ref(null);

const CustomCampaigns = computed(
  () => getters['campaigns/getCustomCampaigns'].value || []
);

const hasNoCustomCampaigns = computed(
  () => CustomCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value?.dialogRef?.open();
};
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.CUSTOM.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.CUSTOM.NEW_CAMPAIGN')"
    @click="handleToggleDialog(true)"
  >
    <template #action>
      <CustomCampaignDialog
        v-if="showCustomCampaignDialog"
        @close="handleToggleDialog(false)"
      />
    </template>
    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <CampaignList
      v-else-if="!hasNoCustomCampaigns"
      :campaigns="CustomCampaigns"
      @delete="handleDelete"
    />
    <CustomCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.CUSTOM.EMPTY_STATE.TITLE')"
      :subtitle="t('CAMPAIGN.CUSTOM.EMPTY_STATE.SUBTITLE')"
      class="pt-14"
    />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
  </CampaignLayout>
</template>
